import React, { FC, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare';

import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from '@buyside/components/presentation/Button/button';
import CheckboxField from '@buyside/components/presentation/Fields/checkboxField';
import InputField from '@buyside/components/presentation/Fields/inputField';
import Modal from '@buyside/components/presentation/Modal/modal';
import SelectField from '@buyside/components/presentation/Fields/selectField';

import { labelize } from '@buyside/util/arrayHelper';
import { sliders } from '@buyside/util/ideaFilterConstants';

import { IdeaFormInput } from '@buyside/util/researchSubmission';

import styles from '../positionsStyles.scss';

const { standard_credit_rating, moody_credit_rating, fitch_credit_rating } = sliders;

const UserCreditDataField: FC<{ index: number }> = ({ index }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    resetField,
    formState: { errors }
  } = useFormContext<IdeaFormInput>();

  const [securityClass, couponType] = useWatch({
    name: [
      `positions.${index}.userDefinedAsset.userCreditData.securityClass`,
      `positions.${index}.userDefinedAsset.userCreditData.couponType`
    ]
  });

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        faIcon={faArrowUpRightFromSquare}
        onClick={() => setIsModalOpen(true)}
        size={BUTTON_SIZES.SMALL}
        text="Fill In Credit Data"
        type={
          errors.positions?.[index]?.userDefinedAsset?.userCreditData
            ? BUTTON_TYPES.DANGER
            : BUTTON_TYPES.PRIMARY
        }
        width={BUTTON_WIDTHS.FULL}
      />
    ),
    [errors.positions, index]
  );

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      renderOpenModalButton={renderOpenModalButton}
      title="Credit Data"
    >
      <div className={styles.creditData}>
        <div className={styles.creditDataRow}>
          <InputField
            endAdornment="%"
            label="Current Yield"
            name={`positions.${index}.userDefinedAsset.userCreditData.currentYield`}
            type="number"
          />
        </div>
        <hr className={styles.creditDataDivider} />
        <div className={styles.creditDataRow}>
          <SelectField
            label="Security Class"
            name={`positions.${index}.userDefinedAsset.userCreditData.securityClass`}
            options={labelize(['secured', 'unsecured'], true)}
            onChangeCallback={value => {
              if (value === 'unsecured') {
                resetField(`positions.${index}.userDefinedAsset.userCreditData.lien`);
                resetField(`positions.${index}.userDefinedAsset.userCreditData.securedBy`);
              }
            }}
          />
        </div>
        <div className={styles.creditDataRow}>
          <InputField
            disabled={securityClass !== 'secured'}
            label="Secured By"
            name={`positions.${index}.userDefinedAsset.userCreditData.securedBy`}
            type="text"
          />
          <SelectField
            disabled={securityClass !== 'secured'}
            label="Lien"
            name={`positions.${index}.userDefinedAsset.userCreditData.lien`}
            options={labelize(['1st_lien', '2nd_lien', '3rd_lien', 'other_lien'], true)}
          />
        </div>
        <hr className={styles.creditDataDivider} />
        <div className={styles.creditDataRow}>
          <SelectField
            label="Seniority"
            name={`positions.${index}.userDefinedAsset.userCreditData.seniority`}
            options={labelize(['senior', 'subordinated', 'junior_subordinated'], true)}
          />
          <InputField
            label="Maturity Date"
            name={`positions.${index}.userDefinedAsset.userCreditData.maturityDate`}
            type="date"
          />
        </div>
        <hr className={styles.creditDataDivider} />
        <div className={styles.creditDataRow}>
          <CheckboxField
            name={`positions.${index}.userDefinedAsset.userCreditData.guaranteed`}
            label="Guaranteed"
          />
          <CheckboxField
            name={`positions.${index}.userDefinedAsset.userCreditData.convertible`}
            label="Convertible"
          />
        </div>
        <hr className={styles.creditDataDivider} />
        <div className={styles.creditDataRow}>
          <SelectField
            isClearable
            label="Coupon Type"
            name={`positions.${index}.userDefinedAsset.userCreditData.couponType`}
            options={labelize(['Fixed', 'Floating', 'Step Down'])}
            onChangeCallback={value => {
              if (!value) {
                resetField(`positions.${index}.userDefinedAsset.userCreditData.couponRate`);
                resetField(`positions.${index}.userDefinedAsset.userCreditData.couponFrequency`);
              }
            }}
          />
        </div>
        <div className={styles.creditDataRow}>
          <InputField
            disabled={!couponType}
            endAdornment="%"
            label="Coupon Rate"
            name={`positions.${index}.userDefinedAsset.userCreditData.couponRate`}
            type="number"
          />
          <SelectField
            disabled={!couponType}
            label="Coupon Frequency"
            name={`positions.${index}.userDefinedAsset.userCreditData.couponFrequency`}
            options={labelize(['monthly', 'quarterly', 'semi-annually', 'annually'], true)}
          />
        </div>
        <hr className={styles.creditDataDivider} />
        <div className={styles.creditDataRow}>
          <InputField
            label="Issue Size (million)"
            name={`positions.${index}.userDefinedAsset.userCreditData.issueSize`}
            type="number"
          />
          <InputField
            label="Amount Outstanding (million)"
            name={`positions.${index}.userDefinedAsset.userCreditData.amountOutstanding`}
            type="number"
          />
        </div>
        <div className={styles.creditDataRow}>
          <SelectField
            label="Embedded Option"
            name={`positions.${index}.userDefinedAsset.userCreditData.embeddedOption`}
            options={labelize(['callable', 'puttable', 'both', 'neither'], true)}
          />
          <SelectField
            label="Moody Credit Rating"
            name={`positions.${index}.userDefinedAsset.userCreditData.moodyCreditRating`}
            options={labelize((moody_credit_rating.increments as string[]).reverse())}
          />
        </div>
        <div className={styles.creditDataRow}>
          <SelectField
            label="Standard Credit Rating"
            name={`positions.${index}.userDefinedAsset.userCreditData.standardCreditRating`}
            options={labelize((standard_credit_rating.increments as string[]).reverse())}
          />
          <SelectField
            label="Fitch Credit Rating"
            name={`positions.${index}.userDefinedAsset.userCreditData.fitchCreditRating`}
            options={labelize((fitch_credit_rating.increments as string[]).reverse())}
          />
        </div>
        <Button
          onClick={() => setIsModalOpen(false)}
          size={BUTTON_SIZES.MEDIUM}
          text="Done"
          type={BUTTON_TYPES.PRIMARY}
        />
      </div>
    </Modal>
  );
};

export default UserCreditDataField;
